import { useOS, AppId } from '@/lib/os-context';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Wifi, Volume2, Battery, BellRing, ChevronUp, Search, Command, Moon, Sun } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { getGlassStyle, isGlassSupported } from '@/lib/glass-theme';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { NotificationCenter } from './notification-center';
import { CalendarPopup } from './calendar-popup';

interface TaskbarProps {
  onSearchClick?: () => void;
}

export function Taskbar({ onSearchClick }: TaskbarProps) {
  const { windows, openWindow, activeWindowId, minimizeWindow, focusWindow, displaySettings, effectiveColorMode, setColorMode } = useOS();
  const [time, setTime] = useState(new Date());
  const [showCalendar, setShowCalendar] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [bouncingApp, setBouncingApp] = useState<AppId | null>(null);
  const [batteryLevel] = useState(87);
  const [notificationCount] = useState(3);
  const isMobile = useIsMobile();

  // Glass theme support
  const isTahoe = displaySettings.theme === 'tahoe';
  const glassEnabled = isTahoe && isGlassSupported();
  const glassStyles = glassEnabled 
    ? getGlassStyle(displaySettings.glassIntensity, displaySettings.glassVariant, effectiveColorMode)
    : {};

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleTaskbarClick = (id: AppId) => {
    const window = windows.find(w => w.id === id);
    
    // Trigger bounce animation
    setBouncingApp(id);
    setTimeout(() => setBouncingApp(null), 500);

    if (window?.isOpen) {
      if (window.id === activeWindowId && !window.isMinimized) {
        minimizeWindow(id);
      } else {
        focusWindow(id);
      }
    } else {
      openWindow(id);
    }
  };

  const openWindows = windows.filter(w => w.isOpen || w.isMinimized);
  const toggleColorMode = () => setColorMode(effectiveColorMode === 'dark' ? 'light' : 'dark');

  return (
    <TooltipProvider>
      <motion.div 
        className={cn(
          "fixed bottom-0 left-0 right-0 flex items-center justify-between px-3 z-9999",
          isTahoe
            ? "h-20 border-none bg-transparent pointer-events-none pb-3"
            : "border-t",
          isMobile ? "h-14" : isTahoe ? "h-20" : "h-12",
          glassEnabled && !isTahoe
            ? "border-white/10 shadow-[0_-8px_32px_rgba(0,0,0,0.3)]"
            : isTahoe
              ? ""
              : effectiveColorMode === 'light'
              ? "bg-white/95 backdrop-blur-2xl border-gray-200/90 shadow-[0_-8px_28px_rgba(15,23,42,0.16)]"
              : "bg-gray-950/95 backdrop-blur-2xl border-white/10 shadow-[0_-8px_28px_rgba(0,0,0,0.45)]"
        )}
        style={glassEnabled && !isTahoe ? glassStyles : {}}
        initial={{ y: 50 }}
        animate={{ y: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      >
        {/* Left Section - Start & Apps */}
        <div className={cn(
          "flex items-center gap-2",
          isTahoe && "tahoe-dock-group pointer-events-auto px-2.5 py-2"
        )}>
          {/* Start Button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <motion.button
                aria-label="Open Profile Menu"
                className={cn(
                  "flex items-center justify-center rounded-lg bg-blue-600 text-white shadow-lg hover:shadow-xl hover:bg-blue-700 transition-all",
                  isMobile ? "w-10 h-10" : "w-9 h-9",
                  isTahoe && "tahoe-app-icon border-white/70 bg-transparent hover:bg-transparent shadow-none text-blue-600"
                )}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => openWindow('profile')}
              >
                <User size={isMobile ? 20 : 18} />
              </motion.button>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p>Start Menu</p>
            </TooltipContent>
          </Tooltip>

          {/* Search - Hidden on mobile */}
          {!isMobile && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button 
                  onClick={onSearchClick}
                  aria-label="Search"
                  className={cn(
                    "hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-colors cursor-pointer",
                    isTahoe
                      ? "tahoe-thin-fill rounded-full border-white/50 hover:bg-white/40"
                      : glassEnabled 
                      ? "bg-white/5 border-white/10 hover:bg-white/10"
                      : effectiveColorMode === 'light'
                        ? "bg-gray-100/90 border-gray-200 hover:bg-gray-100"
                        : "bg-white/10 border-white/10 hover:bg-white/15"
                  )}
                >
                  <Search size={14} className={effectiveColorMode === 'light' ? "text-gray-700" : "text-foreground/80"} />
                  <span className={cn(
                    "text-xs",
                    effectiveColorMode === 'light' ? "text-gray-700" : "text-foreground/80"
                  )}>Search...</span>
                  <div className="flex items-center gap-0.5 ml-2">
                    <kbd className="px-1 py-0.5 bg-background/60 rounded text-[10px] font-mono text-foreground/70">
                      <Command size={10} className="inline" />
                    </kbd>
                    <kbd className="px-1.5 py-0.5 bg-background/60 rounded text-[10px] font-mono text-foreground/70">K</kbd>
                  </div>
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Search (⌘K)</p>
              </TooltipContent>
            </Tooltip>
          )}

          {/* Mobile Search Icon */}
          {isMobile && (
            <motion.button
              aria-label="Search"
              className={cn(
                "flex items-center justify-center w-10 h-10 rounded-lg text-muted-foreground",
                glassEnabled ? "bg-white/5" : "bg-secondary/50"
              )}
              whileTap={{ scale: 0.95 }}
              onClick={onSearchClick}
            >
              <Search size={18} />
            </motion.button>
          )}

          {/* Separator - hidden on mobile */}
          {!isMobile && <div className="w-px h-6 bg-border mx-1" />}

          {/* Open Windows - hidden on mobile */}
          {!isMobile && (
            <div className="flex items-center gap-1">
            <AnimatePresence mode="popLayout">
              {openWindows.map((window) => (
                <Tooltip key={window.id}>
                  <TooltipTrigger asChild>
                    <motion.button
                      layout
                      aria-label={`Open ${window.title}`}
                      onClick={() => handleTaskbarClick(window.id)}
                      className={cn(
                        "relative px-3 h-9 rounded-lg text-xs font-medium transition-all flex items-center gap-2 max-w-[160px]",
                        window.id === activeWindowId && !window.isMinimized
                          ? isTahoe
                            ? "tahoe-thin-fill rounded-full text-foreground shadow-inner"
                            : "bg-accent text-accent-foreground shadow-inner"
                          : isTahoe
                            ? "rounded-full hover:bg-white/24 text-foreground/80 hover:text-foreground"
                            : "hover:bg-accent/50 text-foreground/80 hover:text-foreground"
                      )}
                      initial={{ opacity: 0, scale: 0.8, y: 20 }}
                      animate={{ 
                        opacity: 1, 
                        scale: bouncingApp === window.id ? [1, 1.1, 0.9, 1] : 1,
                        y: bouncingApp === window.id ? [0, -8, 0] : 0 
                      }}
                      exit={{ opacity: 0, scale: 0.8, y: 20 }}
                      transition={{ duration: 0.3 }}
                    >
                      {window.icon && (
                        <span className="w-4 h-4 flex items-center justify-center">
                          {window.icon}
                        </span>
                      )}
                      <span className="truncate hidden sm:inline">{window.title.split(' - ')[0]}</span>
                      
                      {/* Running indicator dot */}
                      <motion.div
                        className={cn(
                          "absolute bottom-0.5 left-1/2 -translate-x-1/2 h-0.5 rounded-full transition-all",
                          window.id === activeWindowId && !window.isMinimized
                            ? "w-6 bg-primary"
                            : "w-2 bg-muted-foreground/50"
                        )}
                        layoutId={`indicator-${window.id}`}
                      />
                    </motion.button>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p>{window.title}</p>
                  </TooltipContent>
                </Tooltip>
              ))}
            </AnimatePresence>
          </div>
          )}
        </div>

        {/* Right Section - System Tray */}
        <div className={cn(
          "flex items-center gap-1",
          isTahoe && "tahoe-dock-group pointer-events-auto px-2 py-1.5"
        )}>
          {/* System Tray Expand - hidden on mobile */}
          {!isMobile && (
            <button aria-label="Expand System Tray" className="p-1.5 rounded-md hover:bg-secondary/50 transition-colors">
              <ChevronUp size={14} className="text-muted-foreground" />
            </button>
          )}

          {!isMobile && <div className="w-px h-6 bg-border mx-1" />}

          {/* System Icons - simplified on mobile */}
          {!isMobile && (
            <div className="flex items-center gap-0.5 px-2 py-1.5 rounded-lg hover:bg-secondary/50 transition-colors cursor-pointer">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="p-1">
                    <Wifi size={14} className="text-foreground/70" />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Connected to WiFi</p>
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="p-1">
                    <Volume2 size={14} className="text-foreground/70" />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Volume: 75%</p>
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="p-1 flex items-center gap-1">
                    <Battery size={14} className="text-foreground/70" />
                    <span className="text-[10px] text-foreground/70 hidden md:inline">{batteryLevel}%</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Battery: {batteryLevel}%</p>
                </TooltipContent>
              </Tooltip>
            </div>
          )}

          {/* Dark / Light Mode Toggle */}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                aria-label={`Switch to ${effectiveColorMode === 'dark' ? 'light' : 'dark'} mode`}
                className={cn(
                  "rounded-md hover:bg-secondary/50 transition-colors",
                  isMobile ? "p-2" : "p-1.5"
                )}
                onClick={toggleColorMode}
              >
                {effectiveColorMode === 'dark' ? (
                  <Sun size={isMobile ? 18 : 14} className="text-foreground/70" />
                ) : (
                  <Moon size={isMobile ? 18 : 14} className="text-foreground/70" />
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Switch to {effectiveColorMode === 'dark' ? 'light' : 'dark'} mode</p>
            </TooltipContent>
          </Tooltip>

          {/* Notifications */}
          <Tooltip>
            <TooltipTrigger asChild>
              <button 
                aria-label={`Notifications - ${notificationCount} new`}
                className={cn(
                  "relative rounded-md hover:bg-secondary/50 transition-colors",
                  isMobile ? "p-2" : "p-1.5"
                )}
                onClick={() => setShowNotifications(!showNotifications)}
              >
                <BellRing size={isMobile ? 18 : 14} className="text-foreground/70" />
                {notificationCount > 0 && (
                  <motion.span
                    className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-red-500 rounded-full text-[9px] text-white flex items-center justify-center font-bold"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring' }}
                  >
                    {notificationCount}
                  </motion.span>
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{notificationCount} new notifications</p>
            </TooltipContent>
          </Tooltip>

          {!isMobile && <div className="w-px h-6 bg-border mx-1" />}

          {/* Clock with Calendar */}
          <Tooltip>
            <TooltipTrigger asChild>
              <motion.button
                className={cn(
                  "flex flex-col items-end rounded-lg hover:bg-secondary/50 transition-colors",
                  isMobile ? "px-2 py-1" : "px-2 py-1"
                )}
                onClick={() => setShowCalendar(!showCalendar)}
                whileTap={{ scale: 0.98 }}
              >
                <span className={cn(
                  "font-medium text-foreground",
                  isMobile ? "text-sm" : "text-xs"
                )}>
                  {format(time, 'h:mm a')}
                </span>
                {!isMobile && (
                  <span className="text-[10px] text-muted-foreground">
                    {format(time, 'MMM d, yyyy')}
                  </span>
                )}
              </motion.button>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p>{format(time, 'EEEE, MMMM do, yyyy')}</p>
              <p className="text-xs text-muted-foreground">Click for calendar</p>
            </TooltipContent>
          </Tooltip>

          {/* Show Desktop - desktop only */}
          {!isMobile && (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="w-1 h-8 bg-border hover:bg-primary/50 transition-colors cursor-pointer rounded-sm ml-1" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Show Desktop</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </motion.div>

      {/* Notification Center Popup */}
      <NotificationCenter 
        isOpen={showNotifications} 
        onClose={() => setShowNotifications(false)} 
      />

      {/* Calendar Popup */}
      <CalendarPopup 
        isOpen={showCalendar} 
        onClose={() => setShowCalendar(false)} 
      />
    </TooltipProvider>
  );
}
